import { baseGtin13, isValidGtin, parseGs1, type Gs1Fields } from './gs1';

/**
 * Normalises keyboard-wedge scanner input the way barcodes are stored: drops an AIM
 * symbology prefix, whitespace and control characters, maps the Turkish İ/ı (a Turkish-Q
 * layout turns a scanned I into one of them) and upper-cases.
 */
export function normalizeScan(raw: string): string {
  return raw
    .trim()
    .replace(/^\][A-Za-z][0-9A-Za-z]/, '')
    .replace(/[\s\u0000-\u001f\u007f]/g, '')
    .replace(/[İı]/g, 'I')
    .toUpperCase();
}

/**
 * A barcode reader types a code as the keys of ITS keyboard layout; the phone or PC reads those keys
 * with its own. Letters and digits sit on the same keys of US and Turkish-Q (i/ı aside, which
 * normalizeScan folds), punctuation does not:
 *
 *   reader on US, computer on Turkish-Q:  '-' → '*'   '_' → '?'   '.' → 'ç'   '/' → '.'
 *   reader on Turkish-Q, computer on US:  '-' → '='   '_' → '+'   '.' → '/'   '/' → '&'   'i' → "'"
 *
 * `shown` maps what arrives (after normalizeScan) back to what the label holds. Nothing in a code
 * proves a mix-up: a seller's barcode or SKU may hold a '+', '*' or '=' of its own ("SET+2", "1+1"),
 * so the code as read is always looked up first and these readings only after it (layoutReadings).
 */
const LAYOUT_MIXUPS: readonly { readonly shown: ReadonlyMap<string, string> }[] = [
  {
    shown: new Map([
      ['*', '-'],
      ['?', '_'],
      ['Ç', '.'],
      ['.', '/'],
    ]),
  },
  {
    shown: new Map([
      ['=', '-'],
      ['+', '_'],
      ['/', '.'],
      ['&', '/'],
      ["'", 'I'],
    ]),
  },
];

/** The code as the label would hold it under one mix-up, or null when the code cannot have come out of it (or it changes nothing). */
function underMixup(code: string, mixup: (typeof LAYOUT_MIXUPS)[number]): string | null {
  const chars = [...code];
  if (!chars.every((ch) => /[A-Z0-9]/.test(ch) || mixup.shown.has(ch))) return null;
  if (!chars.some((ch) => mixup.shown.has(ch))) return null;
  return chars.map((ch) => mixup.shown.get(ch) ?? ch).join('');
}

/**
 * Other ways a normalised code may have been meant under a keyboard-layout mix-up between a barcode
 * reader and the computer it types into, for look-ups that try them after the code itself failed:
 * "YLM*TORBA*35" from a US reader on a Turkish-Q phone is YLM-TORBA-35; a US reader on a Turkish-Q
 * computer turns AB/12 into AB.12, a Turkish-Q reader on a US one AB.12 into AB/12.
 */
export function layoutReadings(code: string): string[] {
  const readings: string[] = [];
  for (const mixup of LAYOUT_MIXUPS) {
    const meant = underMixup(code, mixup);
    if (meant !== null && meant !== code && !readings.includes(meant)) readings.push(meant);
  }
  return readings;
}

/** The spellings a scanned code (or SKU) is looked up by, best first: as read, then its layoutReadings. */
export function scanSpellings(raw: string): string[] {
  const code = normalizeScan(raw);
  return code ? [code, ...layoutReadings(code)] : [];
}

export interface BarcodeCandidates {
  /** Lookup keys for product_barcodes, best first */
  readonly codes: readonly string[];
  /** Present when the scan was a GS1-128 / DataMatrix element string (koli etiketi) */
  readonly gs1: Gs1Fields | null;
  /**
   * GTIN-14 with indicator 1–8: a case. When no barcode matches it directly, the unit GTIN-13
   * is derived from it and one scan counts as a whole case.
   */
  readonly caseGtin: string | null;
}

/**
 * Everything a raw scan could match in the catalog. `layouts`: a code a reader typed also tries its
 * layoutReadings, last (a barcode from a marketplace or a file does not).
 */
export function barcodeCandidates(raw: string, options: { readonly layouts?: boolean } = {}): BarcodeCandidates {
  const gs1 = parseGs1(raw);
  const normalized = normalizeScan(raw);
  const codes: string[] = [];
  const add = (code: string | undefined) => {
    if (code && !codes.includes(code)) codes.push(code);
  };

  const gtin = gs1?.gtin ?? (/^\d{8}$|^\d{12,14}$/.test(normalized) ? normalized : undefined);
  if (gs1 === null) add(normalized);
  if (gtin) {
    add(gtin);
    // GTIN-14 with indicator 0 is the same item as its GTIN-13 (and the reverse)
    if (gtin.length === 14 && gtin.startsWith('0')) add(gtin.slice(1));
    if (gtin.length === 13) add(`0${gtin}`);
  }
  if (gs1 === null && options.layouts) layoutReadings(normalized).forEach(add);
  const caseGtin = gtin && /^[1-8]\d{13}$/.test(gtin) && isValidGtin(gtin) ? gtin : null;
  return { codes, gs1, caseGtin };
}

/** Unit GTIN-13 behind a case GTIN-14 (see BarcodeCandidates.caseGtin). */
export function unitGtinOfCase(caseGtin: string): string {
  return baseGtin13(caseGtin);
}
